import type {
  IOptionTranslationEntry,
  ITranslationEntryInput,
} from '@/api/services/translations.service';
import { ITranslation } from '@/interfaces/ITranslation';

/**
 * «Traducciones» of the editors (plan §5.12, §5.15): per chosen language,
 * the product's or category's name and description, and each saved size's
 * title and description. Rows come from `i{id}-name|desc` (`c{id}-…` for a
 * category) and `oi{sizeId}-title|desc`.
 */
export interface ITranslationText {
  title: string | null;
  description: string | null;
  outdated: boolean;
}

/** What is translated: a product (`i` keys) or a category (`c` keys). */
export type TTranslatedEntity = 'item' | 'category';

/** `{language}` → the entity's text; `{language}|{sizeId}` → a size's. */
export type TTranslationTexts = Record<string, ITranslationText>;

export interface ITranslationChanges {
  /** Languages of the product or category to rewrite. */
  entries: ITranslationEntryInput[];
  /** Its languages whose name and description were both cleared. */
  removedLanguageIds: string[];
  /** Size texts to rewrite (both fields empty deletes them). */
  sizeEntries: IOptionTranslationEntry[];
}

/** Translated when visible: the automatic translation skips hidden products. */
export type TTranslationStatus = 'hidden' | 'pending' | 'ready';

export function sizeTextKey(languageId: string, sizeId: number): string {
  return `${languageId}|${sizeId}`;
}

function emptyText(): ITranslationText {
  return { title: null, description: null, outdated: false };
}

function normalized(value: string | null | undefined): string | null {
  const trimmed = (value ?? '').trim();
  return trimmed ? trimmed : null;
}

function sameText(
  a: ITranslationText | undefined,
  b: ITranslationText | undefined
): boolean {
  return (
    normalized(a?.title) === normalized(b?.title) &&
    normalized(a?.description) === normalized(b?.description)
  );
}

function applyRow(
  text: ITranslationText | undefined,
  field: string,
  row: ITranslation
) {
  if (!text) return;
  if (field === 'desc') text.description = row.value;
  else text.title = row.value;
  if (row.outdated) text.outdated = true;
}

/** Every (language, target) text, filled from the stored rows. */
export function translationTexts(input: {
  entity: TTranslatedEntity;
  entityId: number;
  entityRows: readonly ITranslation[];
  sizeRows: readonly ITranslation[];
  languageIds: readonly string[];
  sizeIds: readonly number[];
}): TTranslationTexts {
  const texts: TTranslationTexts = {};
  for (const languageId of input.languageIds) {
    texts[languageId] = emptyText();
    for (const sizeId of input.sizeIds)
      texts[sizeTextKey(languageId, sizeId)] = emptyText();
  }
  const prefix = input.entity === 'item' ? 'i' : 'c';
  for (const row of input.entityRows) {
    const match = /^([ic])(\d+)-(name|desc)$/.exec(row.key);
    if (match && match[1] === prefix && Number(match[2]) === input.entityId)
      applyRow(texts[row.languageId], match[3], row);
  }
  for (const row of input.sizeRows) {
    const match = /^oi(\d+)-(title|desc)$/.exec(row.key);
    if (match)
      applyRow(
        texts[sizeTextKey(row.languageId, Number(match[1]))],
        match[2],
        row
      );
  }
  return texts;
}

/** Keys whose text differs (the `outdated` flag is not an edit). */
export function changedTextKeys(
  base: TTranslationTexts,
  form: TTranslationTexts
): string[] {
  const keys = new Set([...Object.keys(base), ...Object.keys(form)]);
  return [...keys].filter((key) => !sameText(base[key], form[key]));
}

/** What «Guardar» sends: only the languages and sizes that changed. */
export function translationChanges(
  base: TTranslationTexts,
  form: TTranslationTexts,
  input: { languageIds: readonly string[]; sizeIds: readonly number[] }
): ITranslationChanges {
  const changes: ITranslationChanges = {
    entries: [],
    removedLanguageIds: [],
    sizeEntries: [],
  };
  for (const languageId of input.languageIds) {
    const product = form[languageId];
    if (product && !sameText(base[languageId], product)) {
      const name = normalized(product.title);
      const description = normalized(product.description);
      if (!name && !description) changes.removedLanguageIds.push(languageId);
      else changes.entries.push({ languageId, name, description });
    }
    for (const sizeId of input.sizeIds) {
      const key = sizeTextKey(languageId, sizeId);
      const size = form[key];
      if (!size || sameText(base[key], size)) continue;
      changes.sizeEntries.push({
        optionItemId: sizeId,
        languageId,
        title: normalized(size.title),
        description: normalized(size.description),
      });
    }
  }
  return changes;
}

export function isEmptyTranslationChanges(
  changes: ITranslationChanges
): boolean {
  return (
    !changes.entries.length &&
    !changes.removedLanguageIds.length &&
    !changes.sizeEntries.length
  );
}

/** Product languages with a description but no name (the name is required). */
export function missingNameLanguages(
  form: TTranslationTexts,
  languageIds: readonly string[]
): string[] {
  return languageIds.filter((languageId) => {
    const text = form[languageId];
    return !!text && !normalized(text.title) && !!normalized(text.description);
  });
}

/**
 * The stored texts replace what was not edited; edits stay (they may have
 * been typed while a load or a save was on its way).
 */
export function rebaseTexts(
  base: TTranslationTexts | null,
  form: TTranslationTexts | null,
  stored: TTranslationTexts
): TTranslationTexts {
  const next: TTranslationTexts = {};
  for (const [key, text] of Object.entries(stored)) {
    const edited = base && form && form[key] && !sameText(base[key], form[key]);
    next[key] = edited ? { ...form[key] } : { ...text };
  }
  return next;
}

/** «Traduciendo cambios…» while any text waits for the automatic translation. */
export function translationStatus(input: {
  visible: boolean;
  description: string | null;
  sizes: readonly { id: number; description: string | null }[];
  texts: TTranslationTexts;
  languageIds: readonly string[];
}): TTranslationStatus {
  if (!input.visible) return 'hidden';
  const waiting = (
    text: ITranslationText | undefined,
    description: string | null
  ) =>
    !text ||
    text.outdated ||
    !normalized(text.title) ||
    (!!normalized(description) && !normalized(text.description));
  for (const languageId of input.languageIds) {
    if (waiting(input.texts[languageId], input.description)) return 'pending';
    for (const size of input.sizes)
      if (
        waiting(input.texts[sizeTextKey(languageId, size.id)], size.description)
      )
        return 'pending';
  }
  return 'ready';
}
